import { FC } from 'react';
import { Box, Text, HStack, VStack } from '@chakra-ui/react';
import { Slider } from '@chakra-ui/react';

interface WeightSliderProps {
  label: string;
  description: string;
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  isDisabled?: boolean;
}

/**
 * 重み付けスライダーコンポーネント
 * 非エンジニア向けの直感的なUI
 */
export const WeightSlider: FC<WeightSliderProps> = ({
  label,
  description,
  value,
  onChange,
  min = 0,
  max = 100,
  step = 5,
  isDisabled = false,
}) => {
  return (
    <Box mb={4}>
      <HStack justify="space-between" mb={2}>
        <Text fontWeight="medium" fontSize="md">
          {label}
        </Text>
        <Text fontWeight="bold" fontSize="lg" color="blue.600">
          {value}%
        </Text>
      </HStack>
      <Slider.Root
        value={[value]}
        onValueChange={(details) => onChange(details.value[0])}
        min={min}
        max={max}
        step={step}
        disabled={isDisabled}
      >
        <Slider.Control>
          <Slider.Track>
            <Slider.Range />
          </Slider.Track>
          <Slider.Thumb index={0} />
        </Slider.Control>
      </Slider.Root>
      <Text fontSize="xs" color="gray.500" mt={1}>
        {description}
      </Text>
    </Box>
  );
};

interface WeightSlidersGroupProps {
  weights: {
    geneticDiversity: number;
    breedingHistory: number;
    traitMatch: number;
  };
  onChange: (weights: {
    geneticDiversity: number;
    breedingHistory: number;
    traitMatch: number;
  }) => void;
  autoBalance?: boolean;
  isDisabled?: boolean;
}

/**
 * 3つの重み付けスライダーをグループ化したコンポーネント
 */
export const WeightSlidersGroup: FC<WeightSlidersGroupProps> = ({
  weights,
  onChange,
  autoBalance = true,
  isDisabled = false,
}) => {
  const total = weights.geneticDiversity + weights.breedingHistory + weights.traitMatch;
  const isValid = total === 100;

  const handleChange = (key: keyof typeof weights, newValue: number) => {
    if (autoBalance) {
      // 自動バランス調整
      const oldValue = weights[key];
      const diff = newValue - oldValue;
      const otherKeys = Object.keys(weights).filter((k) => k !== key) as Array<
        keyof typeof weights
      >;

      // 他の2つの値を均等に調整
      const adjustment = Math.round(diff / 2);
      const newWeights = { ...weights, [key]: newValue };

      otherKeys.forEach((otherKey, index) => {
        const adjustedValue =
          index === 0
            ? weights[otherKey] - adjustment
            : weights[otherKey] - (diff - adjustment);
        newWeights[otherKey] = Math.max(0, Math.min(100, adjustedValue));
      });

      // 合計が100になるよう微調整
      const newTotal =
        newWeights.geneticDiversity + newWeights.breedingHistory + newWeights.traitMatch;
      if (newTotal !== 100) {
        const lastKey = otherKeys[otherKeys.length - 1];
        newWeights[lastKey] += 100 - newTotal;
      }

      onChange(newWeights);
    } else {
      onChange({ ...weights, [key]: newValue });
    }
  };

  return (
    <VStack align="stretch" gap={4}>
      <WeightSlider
        label="遺伝的多様性"
        description="近親係数が低いほど高評価。遺伝的な健康を重視します。"
        value={weights.geneticDiversity}
        onChange={(v) => handleChange('geneticDiversity', v)}
        isDisabled={isDisabled}
      />
      <WeightSlider
        label="繁殖実績"
        description="過去の出産データに基づく成功率を評価します。"
        value={weights.breedingHistory}
        onChange={(v) => handleChange('breedingHistory', v)}
        isDisabled={isDisabled}
      />
      <WeightSlider
        label="特徴マッチング"
        description="性格、体格、毛色などの相性を評価します。"
        value={weights.traitMatch}
        onChange={(v) => handleChange('traitMatch', v)}
        isDisabled={isDisabled}
      />

      {/* 合計表示 */}
      <HStack justify="space-between" pt={2} borderTopWidth={1}>
        <Text fontWeight="medium">合計</Text>
        <Text
          fontWeight="bold"
          fontSize="lg"
          color={isValid ? 'green.600' : 'red.600'}
        >
          {total}%
        </Text>
      </HStack>
      {!isValid && (
        <Text fontSize="sm" color="red.500">
          合計が100%になるよう調整してください
        </Text>
      )}
    </VStack>
  );
};
